// B2-GATES (planned-outputs §3, the hold window): a DE's move through a `hold` gate shows on the
// task as one quiet line for its owner — "Riley moved this to Scheduled · pull back until …" —
// with "Pull back" (a modal with an optional note: the task returns to where it was and the DE
// is woken with the note). From `GET tasks/{id}/hold`; nothing open, nothing shown.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Textarea,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HourglassIcon, Undo2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../../api/client";
import { formatDateTime } from "../../format";
import { problemText } from "../../settings/problem-text";
import type { Board } from "../view-engine";
import { workKeys } from "../work-queries";

const holdKey = (co: string, task: string) => [...workKeys.task(co, task), "hold"] as const;
const holdQuery = (api: Api, co: string, task: string) =>
  queryOptions({
    queryKey: holdKey(co, task),
    queryFn: () => api.client.tasks.hold({ params: { co, id: task } }),
  });

export function HoldStrip({
  api,
  co,
  taskId,
  board,
}: {
  api: Api;
  co: string;
  taskId: string;
  board: Board | undefined;
}) {
  const qc = useQueryClient();
  const q = useQuery(holdQuery(api, co, taskId));
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const id = useId();
  const pull = useMutation({
    mutationFn: () =>
      api.client.tasks.pullHold({
        params: { co, id: taskId },
        body: note.trim() ? { note: note.trim() } : {},
      }),
    onSuccess: (out) => {
      setOpen(false);
      qc.setQueryData(holdKey(co, taskId), out);
      void qc.invalidateQueries({ queryKey: workKeys.task(co, taskId) });
    },
  });
  const h = q.data?.hold;
  if (h?.state !== "open") return null;
  const name = (key: string) => board?.status_set.find((s) => s.key === key)?.name ?? key;
  return (
    <section
      aria-label="Held move"
      data-slot="hold-strip"
      className="flex min-h-9 items-center gap-2 border-b border-subtle px-6 py-1.5 text-sm text-muted-foreground max-md:px-4"
    >
      <HourglassIcon aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        {h.held_by_name ?? "A DE"} moved this to {name(h.to_status)} · pull back until{" "}
        {formatDateTime(h.until)}
      </span>
      {h.can_pull ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <Button variant="ghost" size="xs" onClick={() => setOpen(true)}>
            <Undo2Icon aria-hidden="true" />
            Pull back
          </Button>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pull back to {name(h.from_status)}?</DialogTitle>
              <DialogDescription>
                The DE gets your note and is woken to rework it.
              </DialogDescription>
            </DialogHeader>
            <Field>
              <FieldLabel htmlFor={id}>Note (optional)</FieldLabel>
              <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            {pull.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {problemText(pull.error, "The move could not be pulled back.")}
              </p>
            ) : null}
            <DialogFooter>
              <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
              <Button disabled={pull.isPending} onClick={() => pull.mutate()}>
                {pull.isPending ? "Pulling back…" : "Pull back"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </section>
  );
}
